import { defineStore } from 'pinia';
import {
  getAutomationSettings,
  updateAutomationSettings,
  getUnitQuestioningSettings,
  getUnitCheckEnforcementSettings,
  AutomationSettings,
  TemperatureSettings,
  UnitQuestioningSettings,
  UnitCheckEnforcementSettings,
  getTemperatureSettings,
  updateTemperatureSettings
} from '../services/automationService';

interface AutomationStoreState {
  automationSettings: AutomationSettings;
  temperatureSettings: TemperatureSettings;
  loading: boolean;
  error: string | null;
}

export const useAutomationStore = defineStore('automation', {
  state: (): AutomationStoreState => ({
    automationSettings: {
      unit_questioning_enabled: false,
      unit_questioning_skippable: true,
      unit_check_enforcement_enabled: true
    },
    temperatureSettings: {
      setpoint_tolerance: 0.5,
      return_temp_tolerance: 2.5,
      activity_feed_threshold: 5.0,
      questioning_trigger: 10.0,
      yard_check_tolerance: 2.5
    },
    loading: false,
    error: null
  }),
  
  getters: {
    isUnitQuestioningEnabled: (state) => state.automationSettings.unit_questioning_enabled,
    isUnitQuestioningSkippable: (state) => state.automationSettings.unit_questioning_skippable,
    isUnitCheckEnforcementEnabled: (state) => state.automationSettings.unit_check_enforcement_enabled,

    // Temperature settings getters
    setpointTolerance: (state) => state.temperatureSettings.setpoint_tolerance,
    returnTempTolerance: (state) => state.temperatureSettings.return_temp_tolerance,
    activityFeedThreshold: (state) => state.temperatureSettings.activity_feed_threshold,
    questioningTrigger: (state) => state.temperatureSettings.questioning_trigger,
    yardCheckTolerance: (state) => state.temperatureSettings.yard_check_tolerance,
  },
  
  actions: {
    async fetchAutomationSettings() {
      try {
        this.loading = true;
        this.error = null;

        const automationSettings = await getAutomationSettings();
        this.automationSettings = automationSettings;

        return automationSettings;
      } catch (error) {
        console.error('Error fetching automation settings:', error);
        this.error = 'Failed to load automation settings';
        throw error;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Fetch unit check enforcement setting only (accessible to train workers without admin permissions).
     * This is a lighter-weight fetch that only retrieves the setting needed for train completion enforcement.
     */
    async fetchUnitCheckEnforcementSettings(): Promise<UnitCheckEnforcementSettings> {
      try {
        this.loading = true;
        this.error = null;

        const settings = await getUnitCheckEnforcementSettings();

        // Update only the relevant part of the store state
        this.automationSettings.unit_check_enforcement_enabled = settings.unit_check_enforcement_enabled;

        return settings;
      } catch (error) {
        console.error('Error fetching unit check enforcement settings:', error);
        this.error = 'Failed to load unit check enforcement settings';
        throw error;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Fetch unit questioning settings only (accessible to yard check users without admin permissions).
     * This is a lighter-weight fetch that only retrieves the settings needed for unit questioning.
     */
    async fetchUnitQuestioningSettings(): Promise<UnitQuestioningSettings> {
      try {
        this.loading = true;
        this.error = null;

        const settings = await getUnitQuestioningSettings();

        // Update only the relevant parts of the store state
        this.automationSettings.unit_questioning_enabled = settings.unit_questioning_enabled;
        this.automationSettings.unit_questioning_skippable = settings.unit_questioning_skippable;
        this.temperatureSettings.questioning_trigger = settings.questioning_trigger;

        return settings;
      } catch (error) {
        console.error('Error fetching unit questioning settings:', error);
        this.error = 'Failed to load unit questioning settings';
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async updateAutomationSettings(settings: Partial<AutomationSettings>) {
      try {
        this.loading = true;
        this.error = null;
        
        const response = await updateAutomationSettings(settings);
        
        if (response.success && response.data) {
          this.automationSettings = response.data;
        }
        
        return response;
      } catch (error) {
        console.error('Error updating automation settings:', error);
        this.error = 'Failed to update automation settings';
        throw error;
      } finally {
        this.loading = false;
      }
    },
    
    // Convenience methods for toggling specific settings
    async toggleUnitQuestioning() {
      return this.updateAutomationSettings({
        unit_questioning_enabled: !this.automationSettings.unit_questioning_enabled
      });
    },
    
    async toggleUnitQuestioningSkippable() {
      return this.updateAutomationSettings({
        unit_questioning_skippable: !this.automationSettings.unit_questioning_skippable
      });
    },

    async toggleUnitCheckEnforcement() {
      return this.updateAutomationSettings({
        unit_check_enforcement_enabled: !this.automationSettings.unit_check_enforcement_enabled
      });
    },

    async fetchTemperatureSettings() {
      try {
        this.loading = true;
        this.error = null;
        
        const temperatureSettings = await getTemperatureSettings();
        this.temperatureSettings = temperatureSettings;
        
        return temperatureSettings;
      } catch (error) {
        console.error('Error fetching temperature settings:', error);
        this.error = 'Failed to load temperature settings';
        throw error;
      } finally {
        this.loading = false;
      }
    },
    
    async updateTemperatureSettings(settings: Partial<TemperatureSettings>) {
      try {
        this.loading = true;
        this.error = null;
        
        const response = await updateTemperatureSettings(settings);
        
        if (response.success && response.data) {
          this.temperatureSettings = response.data;
        }
        
        return response;
      } catch (error) {
        console.error('Error updating temperature settings:', error);
        this.error = 'Failed to update temperature settings';
        throw error;
      } finally {
        this.loading = false;
      }
    },
    
    // Fetch all settings at once
    async fetchAllSettings() {
      try {
        this.loading = true;
        this.error = null;
        
        const [automationSettings, temperatureSettings] = await Promise.all([
          getAutomationSettings(),
          getTemperatureSettings()
        ]);
        
        this.automationSettings = automationSettings;
        this.temperatureSettings = temperatureSettings;
        
        return { automationSettings, temperatureSettings };
      } catch (error) {
        console.error('Error fetching settings:', error);
        this.error = 'Failed to load settings';
        throw error;
      } finally {
        this.loading = false;
      }
    },
    
    // Reset to default values
    resetToDefaults() {
      this.automationSettings = {
        unit_questioning_enabled: false,
        unit_questioning_skippable: true,
        unit_check_enforcement_enabled: true
      };
      this.temperatureSettings = {
        setpoint_tolerance: 0.5,
        return_temp_tolerance: 2.5,
        activity_feed_threshold: 5.0,
        questioning_trigger: 10.0,
        yard_check_tolerance: 2.5
      };
    }
  }
});